import { animate, style, transition, trigger } from '@angular/animations';
import { DatePipe, KeyValue, KeyValuePipe, LowerCasePipe, NgClass, NgForOf, NgIf } from '@angular/common';
import { Component, ElementRef, HostListener, OnDestroy, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { MatButtonToggle, MatButtonToggleChange, MatButtonToggleGroup } from '@angular/material/button-toggle';
import { MatOptgroup } from '@angular/material/core';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatOption, MatSelect, MatSelectTrigger } from '@angular/material/select';
import { MatSort, MatSortModule } from '@angular/material/sort';
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderCellDef,
  MatHeaderRow,
  MatHeaderRowDef,
  MatRow,
  MatRowDef,
  MatTable,
  MatTableDataSource
} from '@angular/material/table';
import { MatTooltip } from '@angular/material/tooltip';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { forkJoin, Observable, of, Subject } from 'rxjs';
import { concatMap, first, map, mergeMap, pairwise, tap } from 'rxjs/operators';
import { AdminService } from '../../../admin/admin.service';
import { DataRulesFoldersPendoService } from './data-rules-folders-pendo.service';
import { HeaderServiceDirective } from '../../../header/header.service';
import { TransformFileAttachment } from '../../../kyloe-pipes.pipe';
import { RippleDirective } from '../../../ripple.directive';
import { DataToolsApps } from '../../../shared/DataToolsApps';
import { LoadingSnackbarComponent } from '../../../shared/loading-snackbar/loading-snackbar.component';
import { LoadingSnackbarService, SnackMessageNames } from '../../../shared/services/loading-snackbar.service';
import { MessageNotificationService } from '../../../shared/services/message-notification.service';
import { UtilsService } from '../../../shared/services/utils.service';
import { SkeletonComponent } from '../../../shared/skeleton/skeleton.component';
import { DataRulesConfirmDialogComponent } from '../../data-rules-confirm-dialog/data-rules-confirm-dialog.component';
import { DataRulesDataService, DataRulesStaticService } from '../../data-rules-data.service';
import {
  ActionsOperators,
  AllPrimaryEntitiesNames,
  AllPrimaryEntitiesTypes,
  ARCHIVE_FOLDER_NAME,
  CriteriaData,
  DataRuleId,
  IDataRule,
  IDataRulesTasks,
  IFolder,
  IRuleStats,
  IRuleTag,
  ITaggedRule,
  ISidebarFolder,
  RuleData,
  SidebarFolderId,
  TaskState
} from '../../data-rules-model';
import { ISavedDataRuleEventData } from '../../data-rules.component';
import { DataRulesService } from '../../data-rules.service';
import { ErrorModalComponent } from '../../error-modal/error-modal.component';
import { ErrorNotificationService } from '../../error-notification.service';
import { CustomOverlayForDirective, ICustomPositioningConfig } from '../../lib/custom-component-overlay.directive';
import { FolderPopupComponent, IRuleList } from '../../lib/folder-popup/folder-popup.component';
import { MetaDataService } from '../../meta-data.service';
import { appearWidth } from '../../rule/kyloe-animations';
import { ChainData, IRuleInChainData, RuleChainsDataService } from '../../rule/rule-chain/rule-chain-data.service';
import { RuleInChainScreenComponent } from '../../rule/rule-chain/rule-in-chain-screen/rule-in-chain-screen.component';
import { ScheduleData, ScheduleDataService } from '../../rule/rule-schedule/schedule-data.service';
import { SocketManager } from '../../socket-manager';
import { FoldersSidebarComponent } from '../folders-sidebar/folders-sidebar.component';
import { RuleFoldersComponent } from '../rule/rule-folders/rule.component';
import { RuleScheduleFoldersComponent } from '../rule/rule-schedule-folders/rule-schedule.component';
import { DataRulesTagService } from './data-rules-tag.service';
import { IRuleModalData, RuleModalCloseAction, RuleModalComponent } from '../rule/rule-modal/rule-modal.component';
import { DataRulesSearchComponent } from './data-rules-search/data-rules-search.component';
import { TooltipOnTruncateDirective } from '../../../shared/directives/tooltip-on-truncate.directive';
import { ENRICH_CREDIT_EXHAUSTED_TOOLTIP, EnrichmentCreditService } from '../../../enrichment-settings/enrichment-credit.service';

@Component({
  standalone: true,
  selector: 'app-data-rules-folders',
  templateUrl: './data-rules.component.html',
  styleUrls: ['./data-rules.component.scss',
              '../../../../assets/css-imports/tables.scss',
              '../../../../assets/css-imports/material/components/kyloe.mat-table.scss',
              '../../../../assets/css-imports/material/components/kyloe.mat-select.scss'],
  imports: [
    NgClass,
    NgIf,
    NgForOf,
    KeyValuePipe,
    RouterLink,
    LowerCasePipe,
    DatePipe,
    MatButtonToggleGroup,
    MatButtonToggle,
    MatTooltip,
    MatSortModule,
    MatTable,
    MatColumnDef,
    MatHeaderCellDef,
    MatCellDef,
    MatHeaderRowDef,
    MatRowDef,
    MatHeaderCell,
    MatCell,
    MatHeaderRow,
    MatRow,
    NgxSkeletonLoaderModule,
    RuleScheduleFoldersComponent,
    RuleInChainScreenComponent,
    CustomOverlayForDirective,
    LoadingSnackbarComponent,
    RippleDirective,
    TransformFileAttachment,
    SkeletonComponent,
    MatSelect,
    MatSelectTrigger,
    MatOption,
    MatOptgroup,
    MatPaginator,
    FoldersSidebarComponent,
    FolderPopupComponent,
    DataRulesSearchComponent,
    TooltipOnTruncateDirective
  ],
  animations: [
    appearWidth,
    trigger('transitionNewRowInOut', [
      transition(':enter', [
        style({height: '0'}),
        animate('.2s ease-out', style({height: '71px'})),
      ]),
      transition(':leave', [
        style({height: '71px'}),
        animate('.2s ease-out', style({height: '0'})),
      ]),
    ]),
  ]
})

// TODO: Would benefit a refactor, a lot of chunky code, especially ngOnInit

export class DataRulesFoldersComponent implements OnInit, OnDestroy {
  dataRules = new MatTableDataSource<IDataRule>();
  displayedColumns = ['entity', 'name', 'lastRun', 'nextRun', 'totalRuns', 'schedule', 'rowActions'];
  searchTerm = '';
  searchScopeFolder: ISidebarFolder | null = null;
  coreEntities: { [key: string]: number } = {};
  fileAttachmentEntities: { [key: string]: number } = {};
  customObjectEntities: { [key: string]: number } = {};
  additionalEntities: { [key: string]: number } = {};
  protected selectedEntity: AllPrimaryEntitiesNames;
  viewAllSelected = true;
  viewAllNewRuleEntity: AllPrimaryEntitiesNames;
  entityMatSelectHovered = false;
  private allRulesMapped = false;
  newRuleButtonsDisabled = false;
  entityMap: any;
  entitySelectorsMap: any;
  currentRuleExamples: RuleData;
  tasksRunning = [];
  metaLoaded = false;
  rulesLoaded = false;
  ruleSaved = true;

  extraEntitiesExpanded = false;
  dataToolsApps = DataToolsApps;
  schedulingEnabled = false;
  amountOfScheduledRules: number;
  deletingSchedule = false;
  rulesThatInChains: IRuleInChainData[] = [];
  savedRuleEvent: Subject<ISavedDataRuleEventData> = new Subject<ISavedDataRuleEventData>();

  chainedRulesEnabled: boolean;

  readonly SidebarFolderId = SidebarFolderId;

  selectedSidebarFolderId: string = SidebarFolderId.ALL_RULES;
  selectedFolder: ISidebarFolder = { id: SidebarFolderId.ALL_RULES, name: 'All Rules', icon: 'folder_open', count: 0 };

  allRulesList: IRuleList[] = [];
  folderRulesMap = new Map<string, IRuleList[]>();
  folderViewAllCount = 0;

  tags: IRuleTag[] = [];
  // TODO: Can currently have a folder called "Archive" which would merge with the actual Archive
  // Have a separate, specific Archive ID.
  private archiveTagId: string | null = null;

  activeRuleForFolderPopup: IDataRule | null = null;
  private activeAddToFolderTrigger: HTMLElement | null = null;

  sidebarPopupData: { folder: ISidebarFolder; folderRules: IRuleList[] } | null = null;
  sidebarCreateFolderPopupOpen = false;

  @ViewChild(FoldersSidebarComponent) sidebarRef: FoldersSidebarComponent;

  sidebarUserFolders: ISidebarFolder[] = [
    { id: SidebarFolderId.ALL_RULES, name: 'All Rules', icon: 'folder', count: 0 },
  ];

  // System items are virtual folders based on rule attributes (not user tags)
  readonly sidebarSystemItems: ISidebarFolder[] = [
    { id: 'unsorted', name: 'Unsorted Rules', icon: 'folder_off', count: 0, outlinedIcon: true, tooltip: 'Rules that have not yet been sorted into any folders' },
    { id: SidebarFolderId.ARCHIVE, name: ARCHIVE_FOLDER_NAME, icon: 'inventory_2', outlinedIcon: true, count: 0 },
  ];

  readonly sidebarProductFolders = [
    { id: 'kyloe-ai',          name: 'Kyloe AI',          icon: 'auto_awesome', count: 0, outlinedIcon: false },
    { id: 'kyloe-awesomeDocs', name: 'Kyloe AwesomeDocs', icon: 'description',  count: 0, outlinedIcon: true },
  ];

  readonly reservedFolderNames: string[] = [
    'Scheduled Rules',
    'Kyloe AI Assist',
    ...this.sidebarUserFolders.map(f => f.name),
    ...this.sidebarProductFolders.map(f => f.name),
    ...this.sidebarSystemItems.map(f => f.name),
  ];

  ruleInChainOverlayPositioningConfig: ICustomPositioningConfig = {
    originX: 'center',
    originY: 'bottom',
    overlayX: 'center',
    overlayY: 'top',
    offsetY: -24,
    offsetX: -56
  };
  private ruleStatsMap = new Map<string, IRuleStats>();

  @ViewChildren(RuleFoldersComponent)
  rules: QueryList<RuleFoldersComponent>;

  @ViewChild(MatSort)
  set sort(sort: MatSort) {
    this.dataRules.sort = sort;
  }

  protected readonly initialRenderBatchSize = 100;

  @ViewChild(MatPaginator)
  set paginator(paginator: MatPaginator) {
    this.dataRules.paginator = paginator;
  }

  private scrollObserver: IntersectionObserver | null = null;
  private observedSentinelEl: HTMLElement | null = null;

  @ViewChild('rulesScrollSentinel')
  set rulesScrollSentinel(sentinel: ElementRef<HTMLElement> | undefined) {
    const el = sentinel?.nativeElement ?? null;
    if (el === this.observedSentinelEl) {
      return;
    }
    this.observedSentinelEl = el;
    this.scrollObserver?.disconnect();
    this.scrollObserver = null;
    if (el) {
      this.scrollObserver = new IntersectionObserver(entries => {
        if (entries[0]?.isIntersecting) {
          this.loadMoreRows('scrolled-near-bottom');
        }
      }, { rootMargin: '400px' });
      this.scrollObserver.observe(el);
    }
  }

  private static readonly LEADING_SKELETON_ROWS = 5;
  private static readonly TRAILING_SKELETON_ROWS = 10;
  private static readonly AVERAGE_REAL_ROW_HEIGHT_PX = 104;
  private static readonly RENDER_BATCH_INCREMENT = 100;

  protected get remainingRowCount(): number {
    return Math.max(0, this.dataRules.filteredData.length - (this.dataRules.paginator?.pageSize ?? 0));
  }

  protected get leadingSkeletonCount(): number {
    return Math.min(DataRulesFoldersComponent.LEADING_SKELETON_ROWS, this.remainingRowCount);
  }

  protected get trailingSkeletonCount(): number {
    return Math.min(DataRulesFoldersComponent.TRAILING_SKELETON_ROWS, this.remainingRowCount - this.leadingSkeletonCount);
  }

  // Rows are lazy loaded in as you scroll. This adds an empty container with a height imitating the potential rows that will come in.
  // This allows the browser scrollbar to reflect the actual scrolling distance of the table available
  protected get spacerHeightPx(): number {
    const spacerRows = this.remainingRowCount - this.leadingSkeletonCount - this.trailingSkeletonCount;
    return spacerRows * DataRulesFoldersComponent.AVERAGE_REAL_ROW_HEIGHT_PX;
  }

  protected isLoadingMoreRows = false;

  private currentEntityFilter(): string {
    return this.viewAllSelected ? 'all' : (this.selectedEntity ?? 'all');
  }

  private async loadMoreRows(triggerSource: 'scrolled-near-bottom' | 'jumped-to-bottom'): Promise<void> {
    const paginator = this.dataRules.paginator;
    const total = this.dataRules.filteredData.length;
    if (!paginator || paginator.pageSize >= total) {
      return;
    }
    const initialRowsShown = paginator.pageSize;
    const startTime = Date.now();
    this.foldersPendo.trackRowLoadingStarted(
      this.selectedSidebarFolderId, this.selectedFolder?.name, this.currentEntityFilter(), total, initialRowsShown, triggerSource
    );
    this.isLoadingMoreRows = true;
    let chunkCount = 0;
    while (paginator.pageSize < total) {
      paginator._changePageSize(Math.min(paginator.pageSize + DataRulesFoldersComponent.RENDER_BATCH_INCREMENT, total));
      chunkCount++;
      // Ensures the browser is able to render the skeleton rows for visibility of progress
      await new Promise<void>(resolve => setTimeout(resolve));
    }
    this.isLoadingMoreRows = false;
    this.foldersPendo.trackAllRowsLoaded(
      this.selectedSidebarFolderId, this.selectedFolder?.name, this.currentEntityFilter(), total, Date.now() - startTime, chunkCount
    );
  }

  // Cover off if the user presses Ctrl+End to jump to the bottom
  @HostListener('window:scroll')
  protected onWindowScroll(): void {
    const paginator = this.dataRules.paginator;
    const total = this.dataRules.filteredData.length;
    if (!paginator || paginator.pageSize >= total) {
      return;
    }
    const nearBottom = window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 800;
    const bottomMarkerRect = this.observedSentinelEl?.getBoundingClientRect();
    if (nearBottom || (bottomMarkerRect && bottomMarkerRect.top <= window.innerHeight)) {
      this.loadMoreRows('jumped-to-bottom');
    }
  }

  routeParamEntity: AllPrimaryEntitiesNames;
  routeParamRuleIds: DataRuleId[] = [];

  enrichCreditsExhausted = false;
  readonly enrichCreditBlockedTooltip = ENRICH_CREDIT_EXHAUSTED_TOOLTIP;

  private _socketManager: SocketManager<IDataRulesTasks>;
  private coreOrder = ['Candidate', 'ClientContact', 'ClientCorporation', 'JobOrder', 'Opportunity', 'Placement', 'Lead'];

  constructor(public dataRulesDataService: DataRulesDataService,
              private metaDataService: MetaDataService,
              private errorNotificationService: ErrorNotificationService,
              private dialog: MatDialog,
              private adminService: AdminService,
              private headerService: HeaderServiceDirective,
              private route: ActivatedRoute,
              private router: Router,
              public loadingSnackbarService: LoadingSnackbarService,
              public ruleChainsDataService: RuleChainsDataService,
              public scheduleDataService: ScheduleDataService,
              public utilsService: UtilsService,
              private dataRulesService: DataRulesService,
              private dataRulesTagService: DataRulesTagService,
              private messageNotificationService: MessageNotificationService,
              private foldersPendo: DataRulesFoldersPendoService,
              private enrichmentCreditService: EnrichmentCreditService) {
    this._socketManager = new SocketManager<IDataRulesTasks>(this.socketTaskUpdateHandler.bind(this), this.socketErrorHandler.bind(this));
    this.errorHandler();
  }

  // Keyboard Shortcuts
  // - Use Alt + Shift + E to expand/collapse entity button rows
  // - Use Alt + Shift + N to Create a New Rule
  @HostListener('document:keydown', ['$event'])
  protected handleKeydownEvent(event: KeyboardEvent) {
    if (event.altKey && event.shiftKey) {
      if (event.code === 'KeyE') {
        window.scrollTo(0, 0);
        this.extraEntitiesExpanded = !this.extraEntitiesExpanded;
      }
      if (event.code === 'KeyR') {
        this.addNewRule();
      }
    }
    if (event.key === 'Escape') {
      const activeElement = (document.activeElement as HTMLElement)?.tagName?.toLowerCase();
      const isInputActive = activeElement === 'input' || activeElement === 'textarea';
      if (!isInputActive) {
        if (this.activeRuleForFolderPopup) {
          this.closeAddToFolderPopup();
        }
        if (this.sidebarCreateFolderPopupOpen) {
          this.closeSidebarCreateFolderPopup();
        }
      }
    }
  }

  ngOnInit() {
    this.enrichmentCreditService.getCreditBalance().subscribe({
      next: balance => {
        this.enrichCreditsExhausted = balance.balance <= 0;
      }
    });

    this.ruleChainsDataService.usedRules.subscribe(usedRules => {
      this.rulesThatInChains = usedRules;
    });

    console.time('dr-startup');
    this.loadingSnackbarService.show(SnackMessageNames.META_LOADED);
    this.loadingSnackbarService.show(SnackMessageNames.RULES_LOADED);
    const chains$ = this.ruleChainsDataService.initialize(true);
    const schedules$ = this.scheduleDataService.initialize(true);
    const rulesData$ = this.dataRulesDataService.initialize(true);
    const stats$ = this.dataRulesService.getRuleStats().pipe(
      tap(stats => stats.forEach(stat => this.ruleStatsMap.set(stat.ruleId, stat)))
    );
    let meta$ = this.metaDataService.getAllMeta();
    meta$ = meta$.pipe(tap(_ => {
      this.metaLoaded = true;
      this.loadingSnackbarService.hide(SnackMessageNames.META_LOADED);
      this.entityMap = this.metaDataService.getEntitiesLabelsMap();
      this.entitySelectorsMap = this.metaDataService.getEntitiesSelectorsMap();
    }));
    const tags$ = this.dataRulesTagService.getAllTags().pipe(
      concatMap(tags => {
        if (tags.find(t => t.name === ARCHIVE_FOLDER_NAME)) {
          return of(tags);
        }
        return this.dataRulesTagService.createTag(ARCHIVE_FOLDER_NAME).pipe(map(newTag => [...tags, newTag]));
      })
    );
    const taggedRules$ = this.dataRulesTagService.getAllTaggedRules();

    const firstOrder$ = forkJoin({meta: meta$, chains: chains$, schedules: schedules$, stats: stats$, tags: tags$, taggedRules: taggedRules$});
    const secondOrder$ = forkJoin({
      // rulesData initialization requires metaData service to be initialized(getAllMeta()) before
      rulesData: rulesData$
    });

    const actions$ = firstOrder$.pipe(
      mergeMap(({...firstOrderJoins}) => {
        return secondOrder$.pipe(
          map(({...secondOrderJoins}) => {
            return {...firstOrderJoins, ...secondOrderJoins};
          })
        );
      }),
      tap(_ => {
        this.chainedRulesEnabled = this.adminService.checkAppEnabled(DataToolsApps.DATARULES_CHAINED_RULES);
        this.rulesLoaded = true;
        this.loadingSnackbarService.hide(SnackMessageNames.RULES_LOADED);
      })
    );

    actions$.subscribe({
      next: data => {
        this.tags = data.tags;
        this.assignTagsToRules(data.tags, data.taggedRules);
        this.sidebarUserFolders = [
          { id: SidebarFolderId.ALL_RULES, name: 'All Rules', icon: 'folder_open', count: 0 },
          ...data.tags
            .filter(t => t.id !== this.archiveTagId)
            .map(tag => ({ id: tag.id, name: tag.name, icon: tag.icon ?? 'folder', colour: tag.colour || undefined, count: 0, outlinedIcon: true }))
        ];

        this.dataRulesDataService.availableEntities.forEach(entityType => {
          if (this.isCoreEntity(entityType))
            this.coreEntities[entityType] = 0;
          else if (this.isFileAttachmentEntity(entityType))
            this.fileAttachmentEntities[entityType] = 0;
          else if (this.metaDataService.isCustomObjectPrimaryEntity(entityType))
            this.customObjectEntities[entityType] = 0;
          else if (this.metaDataService.isPrimaryEntity(entityType))
            this.additionalEntities[entityType] = 0;
        });

        DataRulesStaticService.withAvailableEntities(
          this.dataRulesDataService.allRules, this.dataRulesDataService.availableEntities
        ).forEach(rule => {
          this.incrementEntityCount(rule.source?.config?.entity);
        });
        this.viewAllNewRuleEntity = this.coreOrder.find(e => Object.keys(this.coreEntities).includes(e)) as AllPrimaryEntitiesNames;
        if (this.adminService.checkAppEnabled(this.dataToolsApps.DATARULES_SCHEDULING))
          this.populateScheduleList();
        console.timeEnd('dr-startup');
        this.route.queryParams.pipe(first()).subscribe(queryParams => this.onRouteChange(null, queryParams));
        this.route.queryParams.pipe(pairwise())
          .subscribe(([previousQueryParams, queryParams]) => this.onRouteChange(previousQueryParams, queryParams));
        this.savedRuleEvent.subscribe((eventData: ISavedDataRuleEventData) => {
          this.onRuleSaved(eventData);
        });
      },
      error: error => {
        this.errorNotificationService.error('Failed to load data', error);
      }
    });
  }

  ngOnDestroy() {
    this._socketManager.closeAll();
    this.closeAddToFolderPopup();
    this.scrollObserver?.disconnect();
  }

  getRuleInChainInfo(ruleId: DataRuleId): IRuleInChainData | null {
    if (this.utilsService.compareInternalDataRuleIds(ruleId, 0)) {
      return null;
    }
    const foundRuleInChain = this.rulesThatInChains.find(
      ruleInChain => this.utilsService.compareInternalDataRuleIds(ruleInChain.id, ruleId)
    );
    return typeof foundRuleInChain === 'undefined'
      ? null
      : foundRuleInChain;
  }

  isRuleInAChain(ruleId: DataRuleId): boolean {
    return !!this.getRuleInChainInfo(ruleId);
  }

  isRuleFirstInAChain(ruleId: DataRuleId): boolean {
    if (ruleId === 0) {
      return false;
    }
    return this.getRuleInChainInfo(ruleId)?.index === 0;
  }

  ruleSchedule(ruleId: string): ScheduleData | null {
    return this.scheduleDataService.getScheduleByRuleId(ruleId);
  }

  getRuleStat(ruleId: DataRuleId): IRuleStats | undefined {
    return this.ruleStatsMap.get(String(ruleId));
  }

  isNextRunUnavailable(ruleId: DataRuleId): boolean {
    const schedule = this.ruleSchedule(ruleId as string);
    return !schedule || schedule.isExpired;
  }

  getNextRunTimestamp(ruleId: DataRuleId): number | null {
    if (this.isNextRunUnavailable(ruleId)) {
      return null;
    }
    const schedule = this.ruleSchedule(ruleId as string);
    const intervalMs = this.scheduleIntervalMs(schedule);
    if (!intervalMs) {
      return null;
    }
    const stat = this.getRuleStat(ruleId);
    const reference = stat?.startTime ?? new Date(schedule.startTime).getTime();
    const now = Date.now();
    if (now <= reference) {
      return reference;
    }
    return reference + Math.ceil((now - reference) / intervalMs) * intervalMs;
  }

  private scheduleIntervalMs(schedule: ScheduleData): number {
    const unitToMs: Record<string, number> = { MINUTES: 60_000, HOURS: 3_600_000, DAYS: 86_400_000 };
    return schedule.period * (unitToMs[schedule.scheduleUnit?.toUpperCase()] ?? 0);
  }

  private populateScheduleList(): void {

    this.checkForMaxScheduledRulesExceeded();
    this.schedulingEnabled = true;
  }

  private populateDataRules(): void {
    if (!this.dataRulesDataService.isInitialisedOnce) {
      return;
    }
    this.dataRules.paginator?._changePageSize(this.initialRenderBatchSize);
    // TODO: Fix - Cloned rules because components are changing the source object, rather than just the form controls
    // Every view, folder and count reads dataRules.data, so filtering here hides these rules everywhere
    const visibleRules = DataRulesStaticService.withAvailableEntities(
      this.dataRulesDataService.allRules, this.dataRulesDataService.availableEntities
    );
    this.dataRules.data = visibleRules.map(rule => {
      rule.isArchived = rule.folders?.some(f => f.id === SidebarFolderId.ARCHIVE) ?? false;
      return { ruleData: rule, viewState: { dirty: false } };
    });

    this.dataRules.filterPredicate = (rule, filter) => {
      const { entity, folderId, search } = filter
        ? JSON.parse(filter) as { entity: string; folderId: string; search: string }
        : { entity: '', folderId: SidebarFolderId.ALL_RULES, search: '' };
      const entityMatch = entity === '' || rule.ruleData.source?.config?.entity === entity;
      const searchMatch = !search || (rule.ruleData.name ?? '').toLowerCase().includes(search.toLowerCase());
      return entityMatch && this.ruleMatchesFolder(rule, folderId) && searchMatch;
    };
    this.tableSorting();
    this.allRulesMapped = true;
    this.applyEntityFilter();
    this.buildSidebarFolders();
  }

  private buildSidebarFolders(): void {
    const folderMap = new Map<string, { name: string; icon: string; count: number; colour?: string; outlinedIcon?: boolean }>();
    let scheduledCount = 0;
    let awesomeDocsCount = 0;
    let aiAssistCount = 0;
    let archivedCount = 0;

    this.sidebarUserFolders.filter(f => f.id !== SidebarFolderId.ALL_RULES).forEach(f => {
      if (!folderMap.has(f.id)) {
        folderMap.set(f.id, { name: f.name, icon: f.icon ?? 'folder', colour: f.colour, count: 0, outlinedIcon: f.outlinedIcon });
      }
    });

    this.dataRules.data.forEach(rule => {
      if (!this.isNextRunUnavailable(rule.ruleData.id)) {
        scheduledCount++;
      }
      const isArchived = rule.ruleData.folders?.some(f => f.id === SidebarFolderId.ARCHIVE) ?? false;
      const steps = rule.ruleData.action?.config?.steps ?? [];
      if (!isArchived && steps.some(s => s.type === ActionsOperators.AWESOMEDOCS)) {
        awesomeDocsCount++;
      }
      if (!isArchived && steps.some(s => s.type === ActionsOperators.AIASSIST)) {
        aiAssistCount++;
      }
      rule.ruleData.folders?.forEach(folder => {
        if (folder.id === SidebarFolderId.ARCHIVE) {
          archivedCount++;
          return;
        }
        const existing = folderMap.get(folder.id);
        if (existing) {
          existing.count++;
        } else {
          const tag = this.tags.find(t => t.id === folder.id);
          folderMap.set(folder.id, {
            name: folder.name,
            icon: tag?.icon ?? 'folder',
            colour: tag?.colour || undefined,
            count: 1,
            outlinedIcon: true
          });
        }
      });
    });

    const unsortedSidebarItem = this.sidebarSystemItems.find(s => s.id === 'unsorted');
    if (unsortedSidebarItem) {
      unsortedSidebarItem.count = this.dataRules.data.filter(rule => !rule.ruleData.folders?.length).length;
    }

    const archiveSidebarItem = this.sidebarSystemItems.find(s => s.id === SidebarFolderId.ARCHIVE);
    if (archiveSidebarItem) {
      archiveSidebarItem.count = archivedCount;
    }

    this.amountOfScheduledRules = scheduledCount;
    this.sidebarProductFolders[0].count = aiAssistCount;
    this.sidebarProductFolders[1].count = awesomeDocsCount;

    this.sidebarUserFolders = [
      { id: SidebarFolderId.ALL_RULES, name: 'All Rules', icon: 'folder_open', count: this.dataRules.data.filter(r => !r.ruleData.folders?.some(f => f.id === SidebarFolderId.ARCHIVE)).length },
      ...Array.from(folderMap.entries()).map(([id, data]) => ({ id, ...data })).sort((a, b) => a.name.localeCompare(b.name))
    ];
    this.updateSelectedFolder();
    this.refreshFolderRulesMap();
    this.rebuildEntityCountsForFolder();
  }

  private ruleMatchesFolder(rule: { ruleData: RuleData }, folderId: string): boolean {
    if (folderId === SidebarFolderId.ALL_RULES) {
      return !rule.ruleData.folders?.some(f => f.id === SidebarFolderId.ARCHIVE);
    }
    if (folderId === 'scheduled') {
      return !this.isNextRunUnavailable(rule.ruleData.id);
    }
    if (folderId === 'unsorted') {
      return !rule.ruleData.folders?.length;
    }
    const isArchived = rule.ruleData.folders?.some(f => f.id === SidebarFolderId.ARCHIVE) ?? false;
    const steps = rule.ruleData.action?.config?.steps ?? [];
    if (folderId === 'kyloe-ai') {
      return !isArchived && steps.some(s => s.type === ActionsOperators.AIASSIST);
    }
    if (folderId === 'kyloe-awesomeDocs') {
      return !isArchived && steps.some(s => s.type === ActionsOperators.AWESOMEDOCS);
    }
    return rule.ruleData.folders?.some(f => f.id === folderId) ?? false;
  }

  private incrementEntityCount(entityType: AllPrimaryEntitiesNames | undefined): void {
    if (typeof entityType === 'undefined') {
      return;
    }
    if (this.isCoreEntity(entityType))
      this.coreEntities[entityType]++;
    else if (this.isFileAttachmentEntity(entityType))
      this.fileAttachmentEntities[entityType]++;
    else if (this.metaDataService.isCustomObjectPrimaryEntity(entityType))
      this.customObjectEntities[entityType]++;
    else if (this.metaDataService.isPrimaryEntity(entityType))
      this.additionalEntities[entityType]++;
  }

  private rebuildEntityCountsForFolder(): void {
    Object.keys(this.coreEntities).forEach(k => this.coreEntities[k] = 0);
    Object.keys(this.fileAttachmentEntities).forEach(k => this.fileAttachmentEntities[k] = 0);
    Object.keys(this.customObjectEntities).forEach(k => this.customObjectEntities[k] = 0);
    Object.keys(this.additionalEntities).forEach(k => this.additionalEntities[k] = 0);

    const folderId = this.selectedSidebarFolderId;
    let total = 0;

    this.dataRules.data.forEach(rule => {
      if (!this.ruleMatchesFolder(rule, folderId)) {
        return;
      }
      total++;
      this.incrementEntityCount(rule.ruleData.source?.config?.entity);
    });

    this.folderViewAllCount = total;
  }

  onFolderSelected(folderId: string): void {
    this.selectedSidebarFolderId = folderId;
    this.searchTerm = '';
    this.viewAllSelected = true;
    this.selectedEntity = undefined;
    this.updateSelectedFolder();
    this.rebuildEntityCountsForFolder();
    this.applyEntityFilterWithoutRowAnimations();
    if (this.routeParamEntity) {
      const { entity: _, ...params } = this.route.snapshot.queryParams;
      this.router.navigate(['datarules'], { queryParams: params, replaceUrl: true });
    }
    this.foldersPendo.trackFolderSelected(folderId, this.selectedFolder?.name ?? '', this.selectedFolder?.count ?? 0, this.userFolderCount());
  }

  private updateSelectedFolder(): void {
    const allFolders = [
      ...this.sidebarUserFolders,
      { id: 'scheduled', name: 'Scheduled Rules', icon: 'schedule', count: this.amountOfScheduledRules ?? 0 },
      ...this.sidebarProductFolders,
      ...this.sidebarSystemItems,
    ];
    this.selectedFolder = allFolders.find(f => f.id === this.selectedSidebarFolderId) ?? this.sidebarUserFolders[0];
  }

  protected suppressRowAnimations = false;

  private applyEntityFilter(): void {
    const entity = this.viewAllSelected ? '' : (this.selectedEntity ?? '');
    this.dataRules.filter = JSON.stringify({ entity, folderId: this.selectedSidebarFolderId, search: this.searchTerm });
    this.updateSearchScope();
    console.log(`KPDR: ${this.viewAllSelected ? 'View All' : this.selectedEntity} Rules`, this.dataRules.filteredData);
  }

  private applyEntityFilterWithoutRowAnimations(): void {
    this.suppressRowAnimations = true;
    this.dataRules.paginator?._changePageSize(this.initialRenderBatchSize);
    this.applyEntityFilter();
    setTimeout(() => this.suppressRowAnimations = false);
  }

  private updateSearchScope(): void {
    const isAllRules = this.selectedSidebarFolderId === SidebarFolderId.ALL_RULES;
    this.searchScopeFolder = isAllRules ? null : (this.selectedFolder ?? null);
  }

  private tableSorting(): void {
    this.dataRules.sortingDataAccessor = (rule, column) => {
      // 'name' is nested under ruleData, not at the root level - also lowercase for case-insensitive sorting
      if (column === 'entity') {
        return (this.entityMap[rule.ruleData.source?.config?.entity] || '').toLowerCase();
      }
      if (column === 'name') {
        return (rule.ruleData.name || '').toLowerCase();
      }
      if (column === 'schedule') {
        const schedule = this.ruleSchedule(rule.ruleData.id as string);
        if (schedule && !schedule.isExpired) {
          const unitToMinutes = { MINUTES: 1, HOURS: 60, DAYS: 1440 };
          const multiplier = unitToMinutes[schedule.scheduleUnit?.toUpperCase()] ?? 0;
          return schedule.period * multiplier;
        }
        return null;
      }
      if (column === 'lastRun') {
        return this.getRuleStat(rule.ruleData.id)?.startTime ?? null;
      }
      if (column === 'totalRuns') {
        return this.getRuleStat(rule.ruleData.id)?.timesRun ?? null;
      }
      if (column === 'nextRun') {
        return this.getNextRunTimestamp(rule.ruleData.id) ?? null;
      }
      return '';
    };

    // Rows with no data sink to the bottom when sorted, regardless of direction
    const nullSinkColumns = ['schedule', 'lastRun', 'nextRun', 'totalRuns'];
    const defaultSortData = this.dataRules.sortData.bind(this.dataRules);
    this.dataRules.sortData = (data, sort) => {
      if (!nullSinkColumns.includes(sort.active) || sort.direction === '') {
        return defaultSortData(data, sort);
      }
      const direction = sort.direction === 'asc' ? 1 : -1;
      return [...data].sort((a, b) => {
        const valA = this.dataRules.sortingDataAccessor(a, sort.active);
        const valB = this.dataRules.sortingDataAccessor(b, sort.active);
        if (valA === null || valB === null) { return valA === null ? 1 : -1; }
        if (valA < valB) { return -direction; }
        if (valA > valB) { return direction; }
        return 0;
      });
    };
  }

  private checkForMaxScheduledRulesExceeded() {

    const validScheduledRules = this.scheduleDataService.allSchedules.filter(
      scheduledRule => !scheduledRule.isExpired
    );

    this.amountOfScheduledRules = validScheduledRules.length;
    this.updateSelectedFolder();

    if ((this.amountOfScheduledRules >= this.adminService.maxNumberOfAutomatedRules) && this.adminService.maxNumberOfAutomatedRules > 0) {
      this.headerService.maxNumberOfAutomatedRulesExceeded.emit(true);
    } else {
      this.headerService.maxNumberOfAutomatedRulesExceeded.emit(false);
    }

  }

  private socketTaskUpdateHandler(task: IDataRulesTasks): void {
    task.state = TaskState[task.state];
    if (!this.isActive(task.state)) {
      console.log(`task update: ${task.id} ${task.state}`, task);
      this._socketManager.close(task.id);
      const index = this.tasksRunning.findIndex(t => t.id === task.id);
      if (index > -1) {
        console.log(`remove task running: ${task.id} at ${index}`);
        this.tasksRunning.splice(index, 1);
      }
    }
  }

  private socketErrorHandler(error: any): void {
    console.log('socket error:', error);
  }

  private isActive(state: TaskState): boolean {
    return state === TaskState.RUNNING || state === TaskState.QUEUED || state === TaskState.STOPPING;
  }

  onEntitySelected(entity: AllPrimaryEntitiesNames): void {
    this.currentRuleExamples = null;
    this.viewAllSelected = false;
    this.selectedEntity = entity;
    this.searchTerm = '';
    this.extraEntitiesExpanded = !this.isCoreEntity(this.selectedEntity);
    this.newRuleButtonsDisabled = false;

    if (this.allRulesMapped) {
      this.applyEntityFilterWithoutRowAnimations();
    } else {
      this.populateDataRules();
    }
  }

  addNewRule(): void {
    const newRule = {ruleData: new RuleData(0, '', this.selectedEntity), viewState: { dirty: true }};
    newRule.ruleData.source.config.criteria = [new CriteriaData('', 'equals')];
    const currentFolder = this.sidebarUserFolders.find(f => f.id === this.selectedSidebarFolderId && f.id !== SidebarFolderId.ALL_RULES);
    if (currentFolder) {
      newRule.ruleData.folders = [{ id: currentFolder.id, name: currentFolder.name }];
    }
    this.dataRules.data = [...this.dataRules.data, newRule];
    if (currentFolder) {
      this.buildSidebarFolders();
      this.applyEntityFilter();
    }
    this.newRuleButtonsDisabled = true;
    this.openRuleModal(newRule);
  }

  addNewRuleViewAll(): void {
    this.selectedEntity = this.viewAllNewRuleEntity;
    this.addNewRule();
  }

  deleteRule(index: number): void {
    const rule = this.dataRules.data[index].ruleData;
    const ruleInChain = this.getRuleInChainInfo(rule.id);

    let dialogTitle = 'Are you sure you want to delete this rule?';
    if (this.ruleSchedule(rule.id as string) && !!ruleInChain)
      dialogTitle = 'This rule is scheduled and in a chain, are you sure you want to delete it?';
    else if (this.ruleSchedule(rule.id as string) && !ruleInChain)
      dialogTitle = 'This rule is scheduled, are you sure you want to delete it?';
    else if (!this.ruleSchedule(rule.id as string) && !!ruleInChain)
      dialogTitle = 'This rule is in a chain, are you sure you want to delete it?';

    const isArchived = rule.folders?.some(f => f.id === SidebarFolderId.ARCHIVE) ?? false;

    const dialogRef = this.dialog.open(DataRulesConfirmDialogComponent, {
      width: '45vw',
      height: 'auto',
      autoFocus: true,
      data: {
        titleIcon: 'bhi-delete-o',
        title: dialogTitle,
        messageIcon: 'bhi-search',
        message: rule.name,
        alternativeButtonText: isArchived ? undefined : ARCHIVE_FOLDER_NAME,
        alternativeButtonIcon: isArchived ? undefined : 'inventory_2',
        outlinedIcons: true
      }
    });

    dialogRef.afterClosed().subscribe((data) => {
      if (!data) {
        return;
      }

      if (data === 'alternative') {
        this.openArchiveConfirmDialog(() => {
          this.archiveRuleData(rule);
        }, { rule, source: 'delete-dialog-alternative', includeNotDeletedNote: true });
        return;
      }

      this.dataRulesDataService.deleteRule(rule.id).subscribe(_ => {
        const entityType = rule.source?.config?.entity;
        if (typeof entityType !== 'undefined' && rule.id !== 0) {
          if (this.isCoreEntity(entityType))
            this.coreEntities[entityType]--;
          else if (this.isFileAttachmentEntity(entityType))
            this.fileAttachmentEntities[entityType]--;
          else if (this.metaDataService.isCustomObjectPrimaryEntity(entityType))
            this.customObjectEntities[entityType]--;
          else if (this.metaDataService.isPrimaryEntity(entityType))
            this.additionalEntities[entityType]--;
        }
        if (this.ruleSchedule(rule.id as string)) {
          this.scheduleDataService.deleteSchedule(rule.id as string).subscribe(() => {
          }, error => console.warn('Error deleting the rule schedule: ', error));
        }
        if (ruleInChain) {
          this.deleteRuleInChain(rule).subscribe(() => {
          }, error => console.warn('Error deleting rule from chain: ', error));
        }
        this.onRuleDeleted(rule.id as string);
        this.dataRules.data = this.dataRules.data.filter(r => !this.utilsService.compareInternalDataRuleIds(r.ruleData.id, rule.id));
        this.newRuleButtonsDisabled = false;
        this.buildSidebarFolders();
        this.applyEntityFilter();
        },
        error => {
          this.errorNotificationService.error('Failed to delete rule', error);
        });
    });
  }

  openScheduled(i: number): void {
    const isAlreadyOpen = this.dataRules.data[i].viewState.scheduleOpen;
    this.dataRules.data.forEach(rule => rule.viewState.scheduleOpen = false);
    if (isAlreadyOpen) {
      return;
    }

    // Allow the user to open a scheduled rule
    if ((this.amountOfScheduledRules >= this.adminService.maxNumberOfAutomatedRules) &&
      this.adminService.maxNumberOfAutomatedRules > 0 && !this.ruleSchedule(this.dataRules.data[i].ruleData.id as string)) {
      const dialogRef = this.dialog.open(DataRulesConfirmDialogComponent, {
        width: '45vw',
        height: 'auto',
        autoFocus: true,
        data: {
          titleIcon: 'warning',
          title: `Maximum ${this.adminService.maxNumberOfAutomatedRules} automated DataRules available reached`,
          messageIcon: 'alarm',
          message: 'You have reached the maximum number of automated DataRules available, please contact support@kyloepartners.com',
          hideCancelButton: true,
          confirmButtonText: 'OK'
        }
      });
      dialogRef.afterClosed().subscribe(data => {
        if (!data) {
          return;
        }
      });
    } else {
      this.dataRules.data[i].viewState.scheduleOpen = true;
    }
  }

  onRuleTitleInteraction(event: MouseEvent, rule: IDataRule): void {
    event.stopPropagation();
    this.foldersPendo.trackRuleNameLinkUsed(event, rule);
  }

  duplicateRule(rule: IDataRule): void {
    const clone = RuleData.clone(rule.ruleData);
    clone.id = 0;
    clone.name = `COPY OF ${clone.name}`;
    const newRule = {ruleData: clone, viewState: { dirty: true }};
    this.dataRules.data = [...this.dataRules.data, newRule];
    this.newRuleButtonsDisabled = true;
    this.openRuleModal(newRule);
  }

  ruleHasEnrichAction(rule: IDataRule): boolean {
    const steps = rule.ruleData.action?.config?.steps ?? [];
    return steps.some(step => step.type === ActionsOperators.ENRICH);
  }

  isRuleEnrichBlocked(rule: IDataRule): boolean {
    return this.enrichCreditsExhausted && this.ruleHasEnrichAction(rule);
  }

  viewExamplesForRule(index: number, ruleID: DataRuleId): void {
    if (this.dataRules.data[index].ruleData.isArchived) {
      return;
    }
    if (this.isRuleEnrichBlocked(this.dataRules.data[index])) {
      return;
    }
    this.currentRuleExamples = this.dataRules.data[index].ruleData;
    this.openRuleModal(this.dataRules.data[index]);
  }

  saveSchedule(scheduleRule: ScheduleData, i: number): void {
    this.dataRules.data[i].viewState.scheduleSaving = true;
    this.dataRules.data[i].viewState.scheduleOpen = false;
    this.scheduleDataService.saveSchedule(scheduleRule).subscribe({
      next: () => {
        this.dataRules.data[i].viewState.scheduleSaving = false;
        this.populateScheduleList();
      },
      error: err => {
        this.dataRules.data[i].viewState.scheduleSaving = false;
        this.messageNotificationService.warning('Could not save Schedule', err);
      }
    });
  }

  deleteSchedule(i: number): void {
    this.dataRules.data[i].viewState.scheduleSaving = true;
    this.deletingSchedule = true;
    this.scheduleDataService.deleteSchedule(this.dataRules.data[i].ruleData.id as string).subscribe({
      next: () => {
        this.dataRules.data[i].viewState.scheduleOpen = false;
        this.dataRules.data[i].viewState.scheduleSaving = false;
        this.deletingSchedule = false;
      },
      error: err => {
        this.dataRules.data[i].viewState.scheduleSaving = false;
        this.deletingSchedule = false;
        this.messageNotificationService.warning('Could not delete Schedule', err);
      }
    });
  }

  private removeScheduleOnArchivedRule(ruleId: DataRuleId): void {
    if (!this.ruleSchedule(ruleId as string)) {
      return;
    }
    this.scheduleDataService.deleteSchedule(ruleId as string).subscribe({
      next: () => { this.buildSidebarFolders(); },
      error: error => this.messageNotificationService.warning('Could not remove Schedule on Archived Rule', error)
    });
  }

  private updateRuleTag(action: 'tag' | 'untag', tagId: string, ruleId: string | number): void {
    const request$ = action === 'tag'
      ? this.dataRulesTagService.tagRule(tagId, String(ruleId))
      : this.dataRulesTagService.deleteTaggedRule(tagId, String(ruleId));
    request$.subscribe({
      error: err => this.messageNotificationService.warning(
        action === 'tag' ? 'Could not add rule to folder' : 'Could not remove rule from folder', err
      )
    });
  }

  private unarchiveRuleData(ruleData: RuleData): void {
    ruleData.folders = [];
    ruleData.isArchived = false;
    this.buildSidebarFolders();
    this.applyEntityFilter();
    this.saveArchiveState(ruleData);
  }

  private archiveRuleData(ruleData: RuleData): void {
    this.untagNonArchiveFolders(ruleData.id, ruleData.folders ?? []);
    ruleData.folders = [{ id: SidebarFolderId.ARCHIVE, name: ARCHIVE_FOLDER_NAME }];
    ruleData.isArchived = true;
    this.buildSidebarFolders();
    this.applyEntityFilter();
    this.removeScheduleOnArchivedRule(ruleData.id);
    this.saveArchiveState(ruleData);
  }

  private untagNonArchiveFolders(ruleId: string | number, folders: IFolder[]): void {
    (folders ?? [])
      .filter(f => f.id !== SidebarFolderId.ARCHIVE)
      .forEach(f => this.updateRuleTag('untag', f.id, ruleId));
  }

  private saveArchiveState(ruleData: RuleData): void {
    // TODO: Will change when we have a specific archive id
    if (!this.archiveTagId) {
      this.messageNotificationService.warning('Archive folder is not configured');
      return;
    }
    const ruleId = String(ruleData.id);
    const request$ = ruleData.isArchived
      ? this.dataRulesTagService.tagRule(this.archiveTagId, ruleId)
      : this.dataRulesTagService.deleteTaggedRule(this.archiveTagId, ruleId);
    request$.subscribe({
      next: () => {
        this.buildSidebarFolders();
        this.applyEntityFilter();
      },
      error: err => this.messageNotificationService.warning('Could not save archive state', err)
    });
  }

  private errorHandler(): void {
    this.errorNotificationService.errors.subscribe(err => {
      console.error('KPDR:', err);
      this.dialog.open(ErrorModalComponent, {height: 'auto', data: {title: err.title, errorMessage: err.message, cause: err.cause}});
    });
  }

  private refreshFolderRulesMap(): void {
    const foldersMap = new Map<string, IRuleList[]>();
    const rulesList: IRuleList[] = [];
    this.dataRules.data.forEach(r => {
      const rule: IRuleList = {
        id: String(r.ruleData.id),
        name: r.ruleData.name ?? '',
        entity: r.ruleData.source?.config?.entity ?? '',
        iconClass: this.renderIcon(this.entitySelectorsMap?.[r.ruleData.source?.config?.entity] ?? '')
      };
      if (!r.ruleData.folders?.some(f => f.id === SidebarFolderId.ARCHIVE)) {
        rulesList.push(rule);
      }
      r.ruleData.folders?.forEach(folder => {
        const existingFolder = foldersMap.get(folder.id) ?? [];
        existingFolder.push(rule);
        foldersMap.set(folder.id, existingFolder);
      });
    });
    this.allRulesList = rulesList;
    this.folderRulesMap = foldersMap;
  }

  onFolderRenamed({ folderId, name }: { folderId: string; name: string }): void {
    const tag = this.tags.find(t => t.id === folderId);
    if (!tag) {
      return;
    }
    this.dataRulesTagService.updateTag({ ...tag, name }).subscribe({
      next: () => {
        tag.name = name;
        const sidebarFolder = this.sidebarUserFolders.find(f => f.id === folderId);
        if (sidebarFolder) {
          sidebarFolder.name = name;
        }
        this.dataRules.data.forEach(rule => {
          const folder = rule.ruleData.folders?.find(f => f.id === folderId);
          if (folder) {
            folder.name = name;
          }
        });
        this.buildSidebarFolders();
        this.foldersPendo.trackFolderRenamed(name);
      },
      error: err => this.messageNotificationService.warning('Could not rename folder', err)
    });
  }

  onFolderDeleted(folder: ISidebarFolder): void {
    this.dataRulesTagService.deleteTag(folder.id).subscribe({
      next: () => {
        const rulesDisconnected = this.dataRules.data.reduce(
          (count, rule) => count + (rule.ruleData.folders?.some(f => f.id === folder.id) ? 1 : 0), 0
        );
        this.tags = this.tags.filter(t => t.id !== folder.id);
        this.dataRules.data.forEach(rule => {
          if (rule.ruleData.folders) {
            rule.ruleData.folders = rule.ruleData.folders.filter(f => f.id !== folder.id);
          }
        });
        this.sidebarUserFolders = this.sidebarUserFolders.filter(f => f.id !== folder.id);
        if (this.selectedSidebarFolderId === folder.id) {
          this.onFolderSelected(SidebarFolderId.ALL_RULES);
        } else {
          this.buildSidebarFolders();
        }
        this.foldersPendo.trackFolderDeleted(folder.name, rulesDisconnected, this.userFolderCount());
      },
      error: err => this.messageNotificationService.warning('Could not delete folder', err)
    });
  }

  onRuleAddedToFolder({ folderId, rule }: { folderId: string; rule: IRuleList }): void {
    this.dataRulesTagService.tagRule(folderId, rule.id).subscribe( () => {
      const tag = this.tags.find(t => t.id === folderId);
      const sidebarFolder = this.sidebarUserFolders.find(f => f.id === folderId);
      const folder: IFolder = { id: folderId, name: tag?.name ?? sidebarFolder?.name ?? '' };
      const target = this.dataRules.data.find(r => String(r.ruleData.id) === rule.id);
      if (!target) {
        return;
      }
      if (!target.ruleData.folders) {
        target.ruleData.folders = [];
      }
      if (!target.ruleData.folders.some(f => f.id === folderId)) {
        target.ruleData.folders = [...target.ruleData.folders, folder];
        this.buildSidebarFolders();
        if (this.selectedSidebarFolderId === folderId) {
          this.applyEntityFilter();
        }
        this.foldersPendo.trackRuleAddedToFolder(folderId, folder.name, rule.entity, 'sidebar-manage-rules');
      }
    });
  }

  onRuleRemovedFromFolder({ folderId, rule }: { folderId: string; rule: IRuleList }): void {
    const target = this.dataRules.data.find(r => String(r.ruleData.id) === rule.id);
    if (!target?.ruleData.folders) {
      return;
    }
    const removedFolderName = target.ruleData.folders?.find(f => f.id === folderId)?.name;
    target.ruleData.folders = target.ruleData.folders.filter(f => f.id !== folderId);
    this.buildSidebarFolders();
    if (this.selectedSidebarFolderId === folderId) {
      this.applyEntityFilter();
    }
    if (folderId !== SidebarFolderId.ARCHIVE) {
      this.updateRuleTag('untag', folderId, target.ruleData.id);
    }
    this.foldersPendo.trackRuleRemovedFromFolder(folderId, removedFolderName, rule.entity, 'sidebar-manage-rules');
  }

  renderIcon(entity: string): string {
    return this.utilsService.renderEntityIcon(entity);
  }

  hasEntries(obj: { [key: string]: number }): boolean {
    return Object.keys(obj).length > 0;
  }

  getSelectedEntityCount(): number {
    return this.coreEntities[this.selectedEntity]
      ?? this.fileAttachmentEntities[this.selectedEntity]
      ?? this.customObjectEntities[this.selectedEntity]
      ?? this.additionalEntities[this.selectedEntity]
      ?? 0;
  }

  onSearch(term: string): void {
    this.searchTerm = term;
    this.applyEntityFilterWithoutRowAnimations();
  }

  searchInAllRules(): void {
    const term = this.searchTerm;
    this.selectedSidebarFolderId = SidebarFolderId.ALL_RULES;
    this.viewAllSelected = true;
    this.selectedEntity = undefined;
    this.updateSelectedFolder();
    this.rebuildEntityCountsForFolder();
    this.onSearch(term);
  }

  private isCoreEntity(entityType): boolean {
    return this.coreOrder.indexOf(entityType) > -1;
  }

  private isFileAttachmentEntity(entityType): boolean {
    return entityType.includes('FileAttachment');
  }

  shiftLeadButton(a: KeyValue<string, number>, b: KeyValue<string, number>): number {
    const ai = this.coreOrder.indexOf(a.key);
    const bi = this.coreOrder.indexOf(b.key);
    return ai - bi;
  }

  private onRuleSaved(eventData: ISavedDataRuleEventData): void {
    const {savedId, unsavedId} = eventData;
    const routeParamRuleIds = this.routeParamRuleIds;
    const routeParamEntity = this.routeParamEntity;
    let newIds;
    if (`${unsavedId}` !== `${savedId}`) {
      newIds = routeParamRuleIds.map(id => `${id}` === '0' ? savedId : id);
    } else {
      newIds = routeParamRuleIds;
    }

    this.ruleChainsDataService.ruleUpdated(savedId).subscribe();

    const rule = this.dataRulesDataService.getRule(savedId);
    const entityType = rule.source.config.entity;

    if (unsavedId === 0) {  // Only increment the rule count if it is a new rule being saved
      if (this.isCoreEntity(entityType))
        this.coreEntities[entityType]++;
      else if (this.isFileAttachmentEntity(entityType))
        this.fileAttachmentEntities[entityType]++;
      else if (this.metaDataService.isCustomObjectPrimaryEntity(entityType))
        this.customObjectEntities[entityType]++;
      else if (this.metaDataService.isPrimaryEntity(entityType))
        this.additionalEntities[entityType]++;
      rule.folders?.filter(f => f.id !== SidebarFolderId.ARCHIVE).forEach(folder => {
        this.updateRuleTag('tag', folder.id, savedId);
      });
    }
    this.newRuleButtonsDisabled = false;
    this.buildSidebarFolders();
    this.navigateToDataRules(routeParamEntity, newIds);
  }

  private onRuleDeleted(deletedId: DataRuleId): void {
    const routeParamRuleIds = this.routeParamRuleIds;
    const routeParamEntity = this.routeParamEntity;
    const newIds = routeParamRuleIds.reduce(
      (accum, id) => this.utilsService.compareInternalDataRuleIds(id, deletedId) ? accum : [...accum, id], []
    );

    this.navigateToDataRules(routeParamEntity, newIds);
  }

  get totalRules(): number {
    return this.dataRulesDataService.allRules.length;
  }

  onViewAllClicked(): void {
    if (this.dataRules.data.some(rule => rule.viewState.dirty)) {
      if (!window.confirm('Unsaved changes. Do you want to continue?')) {
        return;
      }
    }
    this.navigateToDataRules();
  }

  onEntityClicked(entity: AllPrimaryEntitiesNames): void {
    if (this.routeParamEntity !== entity) {
      if (this.dataRules.data.some(rule => rule.viewState.dirty)) {
        if (!window.confirm('Unsaved changes. Do you want to continue?'))
          return;
      }
      this.navigateToDataRules(entity);
    }
  }

  onEntityToggleChange(event: MatButtonToggleChange): void {
    if (event.value === '') {
      this.onViewAllClicked();
    } else {
      this.onEntityClicked(event.value);
    }
  }

  // TODO: This will be removed, only called from spec files. Here for reference for now whilst developing openRuleModal() below
  onRuleClicked(id: DataRuleId) {
    this.currentRuleExamples = null;
    let isSelectedRuleUnsaved = false;
    const isSelectedRule = this.routeParamRuleIds.findIndex(routeId => this.utilsService.compareInternalDataRuleIds(routeId, id)) > -1;
    let newIDs: DataRuleId[];
    if (isSelectedRule) {
      isSelectedRuleUnsaved = this.dataRules.data.some(
        rule => rule.viewState.dirty && this.utilsService.compareInternalDataRuleIds(rule.ruleData.id, id)
      );
      newIDs = this.routeParamRuleIds.filter(routeId => `${routeId}` !== `${id}`);
    } else {
      newIDs = [...this.routeParamRuleIds, id];
    }

    if (isSelectedRuleUnsaved) {
      if (!window.confirm('Unsaved changes. Do you want to continue?'))
        return;
      if (this.utilsService.compareInternalDataRuleIds(id, 0)) {
        this.dataRules.data = this.dataRules.data.filter(r => !this.utilsService.compareInternalDataRuleIds(r.ruleData.id, 0));
        this.newRuleButtonsDisabled = false;
      }
    }

    this.navigateToDataRules(this.routeParamEntity, newIDs);
  }

  // TODO: Will replace onRuleClicked() above
  openRuleModal(rule: IDataRule): void {
    const ruleIndex = this.dataRules.data.indexOf(rule);
    const selectedEntity = this.viewAllSelected
      ? rule.ruleData.source?.config?.entity
      : this.selectedEntity;

    if (!this.utilsService.compareInternalDataRuleIds(rule.ruleData.id, 0)) {
      const foldersParam = this.route.snapshot.queryParams['folders'];
      const openQueryParams: Record<string, string | undefined> = { rule: String(rule.ruleData.id), folders: foldersParam };
      if (this.routeParamEntity) {
        openQueryParams['entity'] = this.routeParamEntity;
      }
      this.router.navigate(['datarules'], { queryParams: openQueryParams, replaceUrl: true });
    }

    const originalRuleDataRef = rule.ruleData;
    let ruleDataSnapshot = RuleData.clone(rule.ruleData);

    this.dialog.open(RuleModalComponent, {
      width: '95vw',
      maxWidth: '1600px',
      maxHeight: '93vh',
      disableClose: false,
      panelClass: 'rule-modal-panel',
      data: {
        dataRule: rule,
        ruleIndex,
        currentRuleExamples: this.currentRuleExamples,
        selectedEntity,
        tasksRunning: this.tasksRunning,
        entityMap: this.entityMap,
        savedRuleEvent: this.savedRuleEvent,
        onArchiveRequested: () => {
          this.openArchiveConfirmDialog(() => {
            this.archiveRuleData(rule.ruleData);
            ruleDataSnapshot = RuleData.clone(rule.ruleData);
          }, { rule: rule.ruleData, source: 'rule-modal-header' });
        },
        onUnarchiveRequested: () => {
          this.unarchiveRuleData(rule.ruleData);
          this.foldersPendo.trackRuleUnarchived(rule.ruleData.source?.config?.entity, 'rule-modal-header');
          ruleDataSnapshot = RuleData.clone(rule.ruleData);
        }
      } as IRuleModalData
    }).afterClosed().subscribe({
      next: (action?: RuleModalCloseAction) => {
        const unsavedRule = this.dataRules.data.find(r =>
          this.utilsService.compareInternalDataRuleIds(r.ruleData.id, 0)
        );
        if (unsavedRule) {
          this.dataRules.data = this.dataRules.data.filter(r =>
            !this.utilsService.compareInternalDataRuleIds(r.ruleData.id, 0)
          );
          this.newRuleButtonsDisabled = false;
        } else {
          if (rule.ruleData === originalRuleDataRef) {
            rule.ruleData = ruleDataSnapshot;
          }
          rule.viewState.dirty = false;
        }
        this.buildSidebarFolders();
        this.applyEntityFilter();
        if (this.router.url.split('?')[0].startsWith('/datarules')) {
          const foldersParam = this.route.snapshot.queryParams['folders'];
          const closeQueryParams: Record<string, string | undefined> = { folders: foldersParam };
          if (this.routeParamEntity) {
            closeQueryParams['entity'] = this.routeParamEntity;
          }
          this.router.navigate(['datarules'], { queryParams: closeQueryParams, replaceUrl: true });
        }
        if (action === RuleModalCloseAction.Duplicate) {
          this.duplicateRule(rule);
        } else if (action === RuleModalCloseAction.Delete) {
          this.deleteRule(this.dataRules.data.indexOf(rule));
        }
      }
    });
  }

  shouldRuleChainInfoClose(): boolean {
    return true;
  }

  onRouteChange(previousParams, newParams): void {

    // Getting the entity and rule query params
    // 'entity' param expected to be undefined OR 'EntityName'-like string
    const routeEntityParam = newParams['entity'];
    this.routeParamEntity = routeEntityParam;

    // 'rule' param expected to be undefined OR 'id,id,id...'-like string
    const routeRuleParam = newParams['rule'];
    this.routeParamRuleIds = routeRuleParam ? routeRuleParam.split(',') : [];

    // 'chain' param expected to be undefined OR '' OR 'id,id,id...'-like string
    const routeChainParam = newParams['chain'];
    const areChainsOpened = typeof routeChainParam === 'string';

    // Checking if we have previous params, if so, getting the previous entity
    const previousEntity: string = previousParams ? previousParams['entity'] : null;
    const entityChanged = (routeEntityParam !== previousEntity) || (previousEntity === null);

    // Checking if the entity selected is a valid entity; if no entity param use View All, if invalid redirect to View All
    if (!AllPrimaryEntitiesTypes.isContains(this.routeParamEntity) || !this.metaDataService.isPrimaryEntity(this.routeParamEntity)) {
      if (this.routeParamEntity) {
        this.navigateToDataRules();
        return;
      }
      if (entityChanged) {
        this.viewAllSelected = true;
        this.currentRuleExamples = null;
        this.newRuleButtonsDisabled = false;
        this.searchTerm = '';
        if (this.allRulesMapped) {
          this.applyEntityFilterWithoutRowAnimations();
        } else {
          this.populateDataRules();
        }
      }
      if (this.routeParamRuleIds.length) {
        this.proceedToNavigatedRules();
      }
      return;
    }

    // Checking the case if chainedRules disabled
    if (!this.chainedRulesEnabled && areChainsOpened) {
      this.navigateToDataRules(this.routeParamEntity, this.routeParamRuleIds);
    }

    if (entityChanged) {
      this.onEntitySelected(this.routeParamEntity);
    }

    if (this.routeParamRuleIds.length) {
      // 'rule' parameter is higher priority than 'chain'
      if (areChainsOpened) {
        this.navigateToDataRules(this.routeParamEntity, this.routeParamRuleIds);
        return;
      }
      this.proceedToNavigatedRules();
    }
  }

  proceedToNavigatedRules(): void {
    if (this.routeParamEntity) {
      const isMissingRule = this.routeParamRuleIds.findIndex(ruleId =>
        this.dataRules.data.findIndex(entityRule => this.utilsService.compareInternalDataRuleIds(entityRule.ruleData.id, ruleId)) === -1
      ) > -1;
      if (isMissingRule) {
        // navigate to specified entity if any of specified rules are not related to that entity
        this.navigateToDataRules(this.routeParamEntity);
        return;
      }
    }

    if (this.dialog.openDialogs.length > 0) {
      return;
    }

    const firstRuleId = this.routeParamRuleIds[0];
    if (!firstRuleId) {
      return;
    }
    const targetRule = this.dataRules.data.find(r =>
      this.utilsService.compareInternalDataRuleIds(r.ruleData.id, firstRuleId)
    );
    if (targetRule) {
      this.openRuleModal(targetRule);
    }
  }

  protected navigateToDataRules(entity?: AllPrimaryEntitiesNames, rules?: DataRuleId[]): void {
    const foldersParam = this.route.snapshot.queryParams['folders'];
    const rulesParam = rules && rules.length > 0 ? rules.join(',') : undefined;
    if (!entity || !AllPrimaryEntitiesTypes.isContains(entity) || !this.metaDataService.isPrimaryEntity(entity)) {
      console.log('Invalid entity type: ', entity);
      this.router.navigate(['datarules'], {queryParams: {rule: rulesParam, folders: foldersParam}});
      return;
    }
    const chainsParam = undefined;
    this.router.navigate(['datarules'], {queryParams: {entity, rule: rulesParam, chain: chainsParam, folders: foldersParam}});
  }

  private navigateToChains(chains?: DataRuleId[]): void {
    const entityParam = this.routeParamEntity;
    const rulesParam = undefined;
    const chainsParam = chains && chains.length > 0 ? chains.join(',') : '';
    this.router.navigate(['datarules'], {queryParams: {entity: entityParam, rule: rulesParam, chain: chainsParam}});
  }

  openAddToFolderPopup(event: MouseEvent | KeyboardEvent, rule: IDataRule): void {
    event.stopPropagation();
    this.removeAddToFolderAnchor();
    const trigger = event.currentTarget as HTMLElement;
    trigger.style.setProperty('anchor-name', '--folder-popup, --folder-popup-arrow');
    this.activeAddToFolderTrigger = trigger;
    this.activeRuleForFolderPopup = rule;
    this.foldersPendo.trackRuleFolderPopupOpened(rule);
  }

  closeAddToFolderPopup(): void {
    this.removeAddToFolderAnchor();
    this.activeRuleForFolderPopup = null;
  }

  onFolderAddedToRule(folder: IFolder): void {
    const rule = this.activeRuleForFolderPopup;
    if (!rule) {
      return;
    }

    const currentFolders = rule.ruleData.folders ?? [];
    if (currentFolders.some(f => f.id === folder.id)) {
      return;
    }
    if (folder.id === SidebarFolderId.ARCHIVE) {
      this.openArchiveConfirmDialog(() => {
        this.archiveRuleData(rule.ruleData);
      }, { rule: rule.ruleData, source: 'sidebar-manage-rules' });
      return;
    }
    if (currentFolders.some(f => f.id === SidebarFolderId.ARCHIVE)) {
      const dialogRef = this.dialog.open(DataRulesConfirmDialogComponent, {
        width: '45vw',
        height: 'auto',
        autoFocus: true,
        data: {
          titleIcon: 'inventory_2',
          title: `Move this Rule out of Archive?`,
          messageIcon: 'folder',
          message: `This will remove the Rule from Archive and add it to the <strong>${folder.name}</strong> Folder.`,
          confirmButtonText: 'Move to Folder',
          confirmButtonIcon: 'folder',
          outlinedIcons: true
        }
      });
      dialogRef.afterClosed().subscribe(confirmed => {
        if (!confirmed) {
          return;
        }
        rule.ruleData.folders = [folder];
        rule.ruleData.isArchived = false;
        this.buildSidebarFolders();
        this.applyEntityFilter();
        this.closeAddToFolderPopup();
        this.saveArchiveState(rule.ruleData);
        this.updateRuleTag('tag', folder.id, rule.ruleData.id);
      });
      return;
    }

    rule.ruleData.folders = [...currentFolders, folder].sort((a, b) => a.name.localeCompare(b.name));
    this.buildSidebarFolders();
    this.updateRuleTag('tag', folder.id, rule.ruleData.id);
    this.foldersPendo.trackRuleAddedToFolder(folder.id, folder.name, rule.ruleData.source?.config?.entity, 'rule-popup-manage-folders');
  }

  onArchiveRequestedFromPopup(): void {
    const rule = this.activeRuleForFolderPopup;
    if (!rule) {
      return;
    }
    this.openArchiveConfirmDialog(() => {
      this.archiveRuleData(rule.ruleData);
    }, { rule: rule.ruleData, source: 'rule-popup-manage-folders' });
  }

  private openArchiveConfirmDialog(onConfirmed: () => void, options: { rule?: RuleData; source?: string; includeNotDeletedNote?: boolean } = {}): void {
    const { rule, source = 'unknown', includeNotDeletedNote = false } = options;
    const archiveContext = this.captureArchiveContext(rule);
    const message = 'Archiving this Rule will:<br>• Remove it from any Folders it is currently in<br>• Stop any active schedules'
      + (includeNotDeletedNote ? '<br>• The rule will <strong>not</strong> be deleted' : '');
    const dialogRef = this.dialog.open(DataRulesConfirmDialogComponent, {
      width: '45vw',
      height: 'auto',
      autoFocus: true,
      data: {
        titleIcon: 'inventory_2',
        title: 'Archive this Rule?',
        messageIcon: 'info',
        message,
        confirmButtonText: ARCHIVE_FOLDER_NAME,
        confirmButtonIcon: 'inventory_2',
        outlinedIcons: true
      }
    });
    dialogRef.afterClosed().subscribe(data => {
      if (!data) {
        return;
      }
      this.foldersPendo.trackRuleArchived(archiveContext, source, true);
      onConfirmed();
    });
  }

  onFolderRemovedFromRule(folder: IFolder): void {
    const rule = this.activeRuleForFolderPopup;
    if (!rule?.ruleData.folders) {
      return;
    }
    rule.ruleData.folders = rule.ruleData.folders.filter(f => f.id !== folder.id);
    if (folder.id === SidebarFolderId.ARCHIVE) {
      rule.ruleData.isArchived = false;
      this.saveArchiveState(rule.ruleData);
    } else {
      this.updateRuleTag('untag', folder.id, rule.ruleData.id);
    }
    this.buildSidebarFolders();
    if (this.selectedSidebarFolderId === folder.id) {
      this.closeAddToFolderPopup();
      this.applyEntityFilter();
    }
    const ruleEntity = rule.ruleData.source?.config?.entity;
    if (folder.id === SidebarFolderId.ARCHIVE) {
      this.foldersPendo.trackRuleUnarchived(ruleEntity);
    } else {
      this.foldersPendo.trackRuleRemovedFromFolder(folder.id, folder.name, ruleEntity, 'rule-popup-manage-folders');
    }
  }

  onSidebarCreateFolderOpened(): void {
    this.sidebarCreateFolderPopupOpen = true;
    this.foldersPendo.trackCreateFolderPopupOpened(this.userFolderCount());
  }

  closeSidebarCreateFolderPopup(): void {
    this.sidebarCreateFolderPopupOpen = false;
    this.sidebarRef.closeCreateFolderPopup();
  }

  onFolderCreated(name: string): void {
    const trimmed = name.trim();
    if (!trimmed) {
      return;
    }
    const rule = this.activeRuleForFolderPopup;
    this.dataRulesTagService.createTag(trimmed).subscribe({
      next: tag => {
        if (!this.tags.some(t => t.id === tag.id)) {
          this.tags = [...this.tags, tag];
        }
        if (rule) {
          const currentFolders = rule.ruleData.folders ?? [];
          if (!currentFolders.some(f => f.id === tag.id)) {
            rule.ruleData.folders = [...currentFolders, { id: tag.id, name: tag.name }];
          }
          this.updateRuleTag('tag', tag.id, rule.ruleData.id);
          this.buildSidebarFolders();
          this.foldersPendo.trackFolderCreated(trimmed, this.userFolderCount(), 'rule-row-add-folder');
          this.foldersPendo.trackRuleAddedToFolder(tag.id, tag.name, rule.ruleData.source?.config?.entity, 'rule-popup-manage-folders');
        } else {
          if (!this.sidebarUserFolders.some(f => f.id === tag.id)) {
            this.sidebarUserFolders = [...this.sidebarUserFolders, { id: tag.id, name: tag.name, icon: 'folder', count: 0, outlinedIcon: true }];
            this.buildSidebarFolders();
          }
          this.foldersPendo.trackFolderCreated(trimmed, this.userFolderCount(), 'sidebar-add-folder');
          this.onFolderSelected(tag.id);
        }
      },
      error: err => {
        this.errorNotificationService.error(`Failed to create Folder "${trimmed}"`, err);
      }
    });
  }

  removeFromFolder(event: MouseEvent | Event, rule: IDataRule, folderId: string): void {
    event.stopPropagation();
    const removedFolderName = rule.ruleData.folders?.find(f => f.id === folderId)?.name;
    if (rule.ruleData.folders) {
      rule.ruleData.folders = rule.ruleData.folders.filter(f => f.id !== folderId);
    }
    if (folderId === SidebarFolderId.ARCHIVE) {
      rule.ruleData.isArchived = false;
      this.saveArchiveState(rule.ruleData);
    } else {
      this.updateRuleTag('untag', folderId, rule.ruleData.id);
    }
    this.buildSidebarFolders();
    if (this.selectedSidebarFolderId === folderId) {
      this.applyEntityFilter();
    }
    this.foldersPendo.trackRuleRemovedFromFolder(folderId, removedFolderName, rule.ruleData.source?.config?.entity, 'rule-row-chip');
  }

  openFolderPopupFromEmptyState(event: MouseEvent | Event): void {
    event.stopPropagation();
    this.sidebarRef.openPopupForActiveFolder();
  }

  private removeAddToFolderAnchor(): void {
    if (this.activeAddToFolderTrigger) {
      this.activeAddToFolderTrigger.style.removeProperty('anchor-name');
      this.activeAddToFolderTrigger = null;
    }
  }

  deleteRuleInChain(rule: RuleData): Observable<void> {
    let chain: ChainData = this.ruleChainsDataService.getChainByRuleId(rule.id);
    this.ruleChainsDataService.removeRuleFromChain(chain.id, chain.ruleIds.findIndex(chainedRuleId => chainedRuleId === rule.id));
    chain = this.ruleChainsDataService.getChain(chain.id);
    return this.ruleChainsDataService.saveChain(chain).pipe(map(() => {
      if (chain.ruleIds.length <= 0) {
        const dialogRef = this.dialog.open(DataRulesConfirmDialogComponent, {
          width: '45vw',
          height: 'auto',
          autoFocus: true,
          data: {
            titleIcon: 'bhi-delete-o',
            title: `This rule was the last remaining rule in chain ${chain.name}. Do you you want to delete the chain?`,
            messageIcon: 'bhi-search',
            message: chain.name
          }
        });
        dialogRef.afterClosed().subscribe(data => {
          if (!data) {
            return;
          }
          this.ruleChainsDataService.deleteChain(chain.id).subscribe();
        });
      }
    }));
  }

  private assignTagsToRules(tags: IRuleTag[], taggedRules: ITaggedRule[]): void {
    const archiveTag = tags.find(t => t.name === ARCHIVE_FOLDER_NAME);
    this.archiveTagId = archiveTag?.id ?? null;

    const ruleTagMap = new Map<string, string[]>();
    taggedRules.forEach(tr => {
      const existing = ruleTagMap.get(tr.ruleId) ?? [];
      existing.push(tr.tagId);
      ruleTagMap.set(tr.ruleId, existing);
    });

    this.dataRulesDataService.allRules.forEach((rule: RuleData) => {
      const tagIds = ruleTagMap.get(String(rule.id)) ?? [];
      if (!rule.folders) {
        rule.folders = [];
      }
      tagIds.forEach(tagId => {
        if (tagId === this.archiveTagId) {
          if (!rule.folders.some(f => f.id === SidebarFolderId.ARCHIVE)) {
            rule.folders.push({ id: SidebarFolderId.ARCHIVE, name: ARCHIVE_FOLDER_NAME });
            rule.isArchived = true;
          }
        } else {
          const tag = tags.find(t => t.id === tagId);
          if (tag && !rule.folders.some(f => f.id === tag.id)) {
            rule.folders.push({ id: tag.id, name: tag.name });
          }
        }
      });
    });
  }

  private captureArchiveContext(rule?: RuleData) {
    return {
      userFolderCountBefore: rule?.folders?.filter(f => f.id !== SidebarFolderId.ARCHIVE).length ?? 0,
      wasScheduled: rule ? !this.isNextRunUnavailable(rule.id) : false,
      ruleEntity: rule?.source?.config?.entity
    };
  }

  private userFolderCount(): number {
    return this.sidebarUserFolders.filter(f => f.id !== SidebarFolderId.ALL_RULES).length;
  }
}
